import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { SupportTicket, SupportTicketStatus, StaffAdmin } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Modal } from '../../components/common/Modal';
import { ColumnDef } from '@tanstack/react-table';
import { Headphones, Send, MessageSquare, Camera, RefreshCw, CheckCircle2 } from 'lucide-react';

const STATUS_FILTERS: { key: SupportTicketStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'agent', label: 'In progress' },
  { key: 'waiting_customer', label: 'Waiting on customer' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

// Ordered set of states an agent can move a ticket to.
const SETTABLE_STATUSES: SupportTicketStatus[] = [
  'open',
  'agent',
  'waiting_customer',
  'resolved',
  'closed',
];

function relTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export const TicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = usePersistentState<SupportTicketStatus | 'all'>('tickets.filter', 'all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [admins, setAdmins] = useState<StaffAdmin[]>([]);

  useEffect(() => {
    // Load platform staff once, for the assignee picker. Non-fatal if it fails.
    api.getAdmins().then(setAdmins).catch(() => setAdmins([]));
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { tickets: rows, openCount: oc } = await api.getTickets(
        filter === 'all' ? undefined : filter,
      );
      setTickets(rows);
      setOpenCount(oc);
    } catch (err) {
      console.error('Failed to load tickets', err);
      setError('Could not load the support queue. Check that you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openThread = async (id: string) => {
    try {
      const full = await api.getTicket(id);
      setSelected(full);
    } catch (err) {
      console.error('Failed to open ticket', err);
    }
  };

  const refreshThread = async () => {
    if (selected) {
      const full = await api.getTicket(selected.id);
      setSelected(full);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !replyText.trim()) return;
    setSending(true);
    try {
      await api.replyTicket(selected.id, replyText.trim());
      setReplyText('');
      await refreshThread();
      loadTickets();
    } catch (err) {
      console.error('Failed to send reply', err);
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status: SupportTicketStatus) => {
    if (!selected) return;
    try {
      const updated = await api.setTicketStatus(selected.id, { status });
      setSelected(updated);
      loadTickets();
    } catch (err) {
      console.error('Failed to change status', err);
    }
  };

  const assign = async (agentId: string) => {
    if (!selected) return;
    try {
      const updated = await api.setTicketStatus(selected.id, { assignedAgentId: agentId });
      setSelected(updated);
      loadTickets();
    } catch (err) {
      console.error('Failed to assign', err);
      setError((err as { message?: string })?.message || 'Failed to assign ticket.');
    }
  };

  const columns = useMemo<ColumnDef<SupportTicket>[]>(
    () => [
      {
        accessorKey: 'subject',
        header: 'Subject',
        cell: (info) => {
          const t = info.row.original;
          const assignee = t.assignedAgentName || t.assignedAgentEmail;
          return (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{t.subject}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                {t.customerName || t.customerEmail || 'Unknown customer'}
                {t.customerPhone ? ` • ${t.customerPhone}` : ''}
              </div>
              <div style={{ fontSize: '11px', color: assignee ? 'var(--primary-light)' : 'var(--text-dim)' }}>
                {assignee ? `Assigned: ${assignee}` : 'Unassigned'}
              </div>
            </div>
          );
        },
      },
      {
        id: 'camera',
        header: 'Camera',
        cell: (info) => {
          const t = info.row.original;
          if (!t.deviceName && !t.deviceSerial) {
            return <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>—</span>;
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Camera size={13} color="var(--text-dim)" />
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-main)' }}>{t.deviceName || t.deviceSerial}</div>
                {t.reason && (
                  <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{t.reason.replace(/_/g, ' ')}</div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
      },
      {
        accessorKey: 'messageCount',
        header: 'Msgs',
        cell: (info) => (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{info.getValue() as number}</span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last activity',
        cell: (info) => (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {relTime(info.getValue() as string)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (info) => (
          <button className="btn btn-secondary btn-sm" onClick={() => openThread(info.row.original.id)}>
            <MessageSquare size={13} />
            <span>Open</span>
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <Headphones size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Support Queue</h2>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            Tickets customers raise in the app. Each opens the conversation and, when the ticket is about a
            camera, its live status.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: openCount > 0 ? 'var(--warning-text, #f59e0b)' : 'var(--success-text)' }}>
          <span style={{ fontWeight: 600 }}>{openCount}</span>
          <span>needing attention</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                fontSize: '12px', padding: '5px 12px', borderRadius: '999px', cursor: 'pointer',
                border: `1px solid ${active ? 'var(--primary-light)' : 'var(--border-dim)'}`,
                background: active ? 'rgba(56,189,248,0.12)' : 'transparent',
                color: active ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: active ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          );
        })}
        <button
          onClick={loadTickets}
          disabled={loading}
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '12px', padding: '5px 12px', borderRadius: '6px', cursor: loading ? 'default' : 'pointer',
            border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)',
          }}
        >
          <RefreshCw size={13} className={loading ? 'spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
            color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px',
            marginBottom: '16px', fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <DataTable
        storageKey="tickets"
        data={tickets}
        columns={columns}
        searchPlaceholder="Search subject, customer, camera…"
        exportFilename="mwm_support_tickets.csv"
      />

      <Modal
        isOpen={!!selected}
        onClose={() => { setSelected(null); setReplyText(''); }}
        title={selected?.subject || 'Ticket'}
        subtitle={
          selected
            ? `${selected.customerName || selected.customerEmail || 'Customer'}${selected.customerPhone ? ' • ' + selected.customerPhone : ''}`
            : ''
        }
        size="large"
      >
        {selected && (
          <div>
            {/* Camera context + live state */}
            {(selected.deviceName || selected.deviceSerial) && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                  background: 'rgba(2,132,199,0.08)', border: '1px solid rgba(56,189,248,0.25)',
                  borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: '14px', flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Camera size={15} color="var(--primary-light)" />
                  <div>
                    <div style={{ fontSize: '12.5px', color: 'var(--text-main)', fontWeight: 600 }}>
                      {selected.deviceName || selected.deviceSerial}
                    </div>
                    {selected.deviceSerial && selected.deviceName && (
                      <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{selected.deviceSerial}</div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                  {typeof selected.deviceOnline === 'boolean' ? (
                    <span style={{ color: selected.deviceOnline ? 'var(--success-text)' : 'var(--danger-text)' }}>
                      {selected.deviceOnline ? '● Online now' : '● Offline'}
                    </span>
                  ) : (
                    <span>No health signal</span>
                  )}
                  {selected.deviceLastSeen && (
                    <span> · last seen {relTime(selected.deviceLastSeen)}</span>
                  )}
                </div>
              </div>
            )}

            {/* Status + assignment controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>Status:</span>
              <select
                className="input"
                value={selected.status}
                onChange={(e) => changeStatus(e.target.value as SupportTicketStatus)}
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
              >
                {SETTABLE_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                ))}
              </select>

              <span style={{ fontSize: '12px', color: 'var(--text-dim)', marginLeft: '4px' }}>Assignee:</span>
              <select
                className="input"
                value={selected.assignedAgentId || 'none'}
                onChange={(e) => assign(e.target.value)}
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px', maxWidth: '220px' }}
              >
                <option value="none">Unassigned</option>
                {/* If the current assignee isn't in the staff list (edge case), still show them. */}
                {selected.assignedAgentId &&
                  !admins.some((a) => a.id === selected.assignedAgentId) && (
                    <option value={selected.assignedAgentId}>
                      {selected.assignedAgentName || selected.assignedAgentEmail || 'Current assignee'}
                    </option>
                  )}
                {admins.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.email}</option>
                ))}
              </select>

              {selected.status !== 'resolved' && (
                <button className="btn btn-secondary btn-sm" onClick={() => changeStatus('resolved')}>
                  <CheckCircle2 size={13} />
                  <span>Resolve</span>
                </button>
              )}
            </div>

            {/* Thread */}
            <div style={{ maxHeight: '320px', overflowY: 'auto', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(selected.messages ?? []).map((msg) => {
                const isAgent = msg.authorRole === 'agent';
                return (
                  <div
                    key={msg.id}
                    style={{
                      alignSelf: isAgent ? 'flex-end' : 'flex-start',
                      maxWidth: '82%',
                      background: isAgent ? 'rgba(2,132,199,0.2)' : 'rgba(30,41,59,0.7)',
                      border: '1px solid var(--border-dim)',
                      borderRadius: 'var(--radius-md)', padding: '9px 13px',
                    }}
                  >
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                      <strong>{msg.authorName || (isAgent ? 'Support agent' : 'Customer')}</strong>
                      <span>{new Date(msg.createdAt).toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize: '12.5px', color: 'var(--text-main)', whiteSpace: 'pre-wrap' }}>
                      {msg.body}
                    </div>
                  </div>
                );
              })}
              {(selected.messages ?? []).length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--text-dim)', textAlign: 'center', padding: '12px' }}>
                  No messages yet.
                </div>
              )}
            </div>

            {/* Reply */}
            <form onSubmit={handleSendReply} style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                className="input"
                style={{ flex: 1 }}
                placeholder="Reply to the customer…"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={sending}>
                <Send size={14} />
                <span>{sending ? 'Sending…' : 'Reply'}</span>
              </button>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
};
